import { getDisplayName } from '@cascade/core/auth';
import {
  isSensitiveInformationError,
  isUserMessage,
} from '@cascade/react/conversations';
import type { TAskPayload, TMessage } from '@cascade/react/conversations';
import { useI18n } from '@cascade/react/i18n';
import { useUser } from '@cascade/react/user';
import { Chat, Text } from '@cascade/ui-library';
import type { TChatRef } from '@cascade/ui-library';
import { useRef } from 'react';
import type { ReactNode } from 'react';
import { buildChatMessage, findRefusedQuestion } from './ChatThread.helpers.ts';
import classes from './ChatThread.module.scss';
import type { TChatThreadProps } from './ChatThread.types.ts';
import { ConfirmDialog } from '#components/confirm-dialog/index.ts';

export const ChatThread = ({
  messages,
  hasPendingAnswer,
  suggestions,
  isDisabled,
  isCompact,
  controlButtons,
  stagingArea,
  renderBeforeContent,
  renderAfterContent,
  onAsk,
  onRemoveRequest,
}: TChatThreadProps) => {
  const { translate, translateError } = useI18n();
  const { user } = useUser();
  const chatRef = useRef<TChatRef>(null);

  const userName = getDisplayName(user);
  const agentName = translate('agent.name');
  const refusedQuestion = findRefusedQuestion(messages);

  const waitingLabels = [
    translate('agent.waiting.cascading'),
    translate('agent.waiting.analyzing'),
    translate('agent.waiting.thinking'),
    translate('agent.waiting.looking'),
  ];

  const getAfterContent = (message: TMessage): ReactNode => {
    if (isUserMessage(message) || message.status !== 'failed') {
      return renderAfterContent?.(message);
    }

    const failureMessage = message.message
      ? translate('agent.incomplete')
      : translateError(message.error);

    return <Text type='body-2' color='text-danger' content={failureMessage} />;
  };

  const visibleMessages = messages.filter(
    (message) => !isSensitiveInformationError(message.error),
  );

  const chatMessages = visibleMessages.map((message) => {
    const chatMessage = buildChatMessage(message, {
      userName,
      agentName,
      waitingLabels,
    });

    const beforeContent = renderBeforeContent?.(message);
    const afterContent = getAfterContent(message);

    return { ...chatMessage, beforeContent, afterContent };
  });

  const send = (payload: TAskPayload) => {
    onAsk(payload).catch(() => undefined);
  };

  const ask = (question: string) => {
    send({ question });
  };

  const resendQuestion = (question: TMessage) => {
    onRemoveRequest(question.requestId ?? '');
    send({
      question: question.message ?? '',
      files: question.files ?? [],
      force: true,
    });
  };

  const dismissQuestion = (question: TMessage) => {
    onRemoveRequest(question.requestId ?? '');

    if (chatRef.current?.getValue()) {
      return;
    }

    chatRef.current?.setValue(question.message ?? '');
  };

  return (
    <>
      <Chat
        ref={chatRef}
        className={classes['chat-thread']}
        compact={isCompact}
        messages={chatMessages}
        suggestions={hasPendingAnswer ? undefined : suggestions}
        placeholder={translate('agent.ask')}
        isLoading={hasPendingAnswer}
        disabled={isDisabled}
        controlButtons={controlButtons}
        stagingArea={stagingArea}
        onSendMessage={ask}
      />
      {refusedQuestion ? (
        <ConfirmDialog
          title={translate('agent.sensitiveInformation.title')}
          description={translate('agent.sensitiveInformation.description')}
          notice={translate('agent.sensitiveInformation.notice')}
          confirmLabel={translate('agent.sensitiveInformation.confirm')}
          cancelLabel={translate('agent.sensitiveInformation.cancel')}
          closeLabel={translate('agent.sensitiveInformation.close')}
          onConfirm={() => resendQuestion(refusedQuestion)}
          onClose={() => dismissQuestion(refusedQuestion)}
        />
      ) : null}
    </>
  );
};
